import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Envelope, Phone, MapPin, ArrowUpRight, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MELOUTE_URL = 'https://www.meloute.com/about-company.html';

const ContactSection = () => {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact/submit`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-5 py-3.5 outline-none transition-all text-[15px] text-[#1C1917] placeholder:text-stone-400";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-2";

  const cards = [
    { icon: Envelope, label: 'Email', value: 'support@thenovafoods.com', href: 'mailto:support@thenovafoods.com', subtitle: 'We reply within one business day' },
    { icon: Phone, label: 'Phone', value: '+91 99029 93067', href: 'tel:+919902993067', subtitle: 'Mon – Sat, 9am to 8pm IST' },
    { icon: MapPin, label: 'Location', value: 'Bangalore, Karnataka', href: null, subtitle: 'Cloud Kitchen · Corporate Catering · Fitness Meal Plans' },
  ];

  return (
    <section
      id="contact"
      className="relative px-6 py-24 md:py-32 bg-[#FAFAF7] overflow-hidden"
      data-testid="contact-section"
      aria-labelledby="contact-heading"
    >
      {/* decorative background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-24 -left-24 w-96 h-96 bg-[#34D399]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 right-0 w-[500px] h-[500px] bg-[#1A4331]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* LEFT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#1A4331] mb-6">— Contact</p>
            <h2 id="contact-heading" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1C1917] font-heading mb-6 leading-[1.05]">
              Let&apos;s build something <span className="italic text-[#1A4331]">healthy</span>.
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-stone-600 max-w-[500px] mb-10">
              Whether you&apos;re looking for healthy meal subscriptions, corporate catering, office lunch programs, or customised nutrition plans, our team is ready to help.
              <br /><br />
              We&apos;ll respond within one business day.
            </p>

            {/* Contact cards */}
            <div className="space-y-4">
              {cards.map((c, i) => {
                const Icon = c.icon;
                const Wrapper = c.href ? 'a' : 'div';
                return (
                  <motion.div
                    key={c.label}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
                  >
                    <Wrapper
                      {...(c.href ? { href: c.href } : {})}
                      className="group flex items-start gap-4 bg-white border border-stone-100 rounded-[20px] p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-[#1A4331]/30 transition-all duration-300"
                      data-testid={`contact-card-${c.label.toLowerCase()}`}
                    >
                      <div className="w-12 h-12 rounded-2xl border border-[#1A4331]/25 bg-[#1A4331]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1A4331] transition-colors duration-300">
                        <Icon size={22} weight="regular" className="text-[#1A4331] group-hover:text-[#34D399] group-hover:scale-110 transition-all duration-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-1">{c.label}</p>
                        <p className="text-lg font-semibold text-[#1C1917] leading-tight truncate">{c.value}</p>
                        {c.subtitle && <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">{c.subtitle}</p>}
                      </div>
                    </Wrapper>
                  </motion.div>
                );
              })}
            </div>

            {/* Parent company */}
            <motion.a
              href={MELOUTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.35 }}
              className="mt-6 block group relative bg-gradient-to-br from-[#1A4331] via-[#204d38] to-[#2A5A44] text-white rounded-[24px] p-6 md:p-7 overflow-hidden hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-[#1A4331]/25 transition-all duration-300"
              data-testid="parent-company-card"
            >
              <div className="pointer-events-none absolute -top-16 -right-16 w-52 h-52 bg-[#34D399]/25 rounded-full blur-3xl" />
              <div className="relative">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#34D399] mb-2">Powered by</p>
                <h3 className="text-2xl md:text-3xl font-medium font-heading tracking-tight text-white mb-2">Meloute Technologies</h3>
                <p className="text-sm text-white/75 leading-relaxed mb-4 max-w-md">
                  Nova Foods is proudly designed, developed and powered by Meloute Technologies.
                </p>
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold group-hover:bg-white group-hover:text-[#1A4331] transition-all">
                  Visit Parent Company <ArrowUpRight size={14} weight="bold" />
                </span>
              </div>
            </motion.a>
          </motion.div>

          {/* RIGHT — Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="bg-white border border-stone-100 rounded-[28px] p-7 md:p-10 shadow-xl shadow-stone-200/40" data-testid="contact-form-card">
              {!submitted ? (
                <>
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-stone-500 mb-3">Send us a message</p>
                  <h3 className="text-2xl md:text-3xl font-medium font-heading text-[#1C1917] tracking-tight mb-6">Tell us where you&apos;re headed.</h3>

                  <form onSubmit={handleSubmit} className="space-y-5" aria-label="Contact form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className={labelCls}>Full Name *</label>
                        <input id="contact-name" type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} required data-testid="contact-name-input" />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className={labelCls}>Email *</label>
                        <input id="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} required data-testid="contact-email-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-phone" className={labelCls}>Phone</label>
                        <input id="contact-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="contact-phone-input" />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className={labelCls}>Subject</label>
                        <input id="contact-subject" type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Corporate catering" className={inputCls} data-testid="contact-subject-input" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className={labelCls}>Message *</label>
                      <textarea id="contact-message" rows="5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} placeholder="Tell us what you're looking for…" required data-testid="contact-message-input" />
                    </div>

                    <button type="submit" disabled={submitting} className="w-full bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white px-6 py-4 rounded-full font-semibold text-[15px] hover:shadow-2xl hover:shadow-[#1A4331]/25 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="contact-submit-btn">
                      {submitting ? (
                        <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Sending…</>
                      ) : (
                        <>Send Message <PaperPlaneTilt size={16} weight="fill" /></>
                      )}
                    </button>
                    <p className="text-xs text-stone-500 text-center">Or email us directly at <a href="mailto:support@thenovafoods.com" className="text-[#1A4331] font-semibold hover:underline">support@thenovafoods.com</a></p>
                  </form>
                </>
              ) : (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="text-center py-10" data-testid="contact-thanks">
                  <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center shadow-lg shadow-[#10B981]/25">
                    <CheckCircle size={44} weight="fill" className="text-white" />
                  </div>
                  <h3 className="text-3xl font-medium font-heading text-[#1C1917] tracking-tight mb-3">Thank you!</h3>
                  <p className="text-base text-stone-600 max-w-sm mx-auto mb-6 leading-relaxed">
                    We&apos;ve received your message and will get back to you within one business day.
                  </p>
                  <button onClick={() => { setSubmitted(false); setForm({ full_name: '', email: '', phone: '', subject: '', message: '' }); }} className="text-sm text-[#1A4331] font-semibold hover:underline" data-testid="contact-send-another">
                    Send another message →
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
